import { css } from 'lit';
import { APIConfig } from '@/config/ConfigCARSx';
import ConfigCCTV, { ConfigCCTVApi } from '@/config/ConfigCCTV';
import APIRequest, { getAPIHeaders } from '@/redux/APIRequest';
import HTTPMethod from 'http-method-enum';
import { clamp, isValidNumber } from '@/utils/utils';

/** Assumed max optical zoom ratio for perceptual zNorm mapping (tunable). */
export const ONVIF_ASSUMED_MAX_ZOOM_RATIO = 20;

/**
 * Maps ONVIF zNorm (linear in magnification) to a perceptual 0–1 position
 * where equal steps are equal multiplicative zoom.
 */
export const zoomNormToPerceptual = (zNorm: number): number => {
	const z = clamp(zNorm, 0, 1);
	const R = ONVIF_ASSUMED_MAX_ZOOM_RATIO;
	return Math.log(1 + z * (R - 1)) / Math.log(R);
};

/**
 * Maps a perceptual 0–1 position back to ONVIF zNorm.
 */
export const perceptualToZoomNorm = (t: number): number => {
	const perceptual = clamp(t, 0, 1);
	const R = ONVIF_ASSUMED_MAX_ZOOM_RATIO;
	return (R ** perceptual - 1) / (R - 1);
};

/**
 * Adjusts zoom by a perceptual delta, returning the resulting zNorm.
 */
export const adjustZoomPerceptually = (zNorm: number, deltaT: number): number => {
	return perceptualToZoomNorm(zoomNormToPerceptual(zNorm) + deltaT);
};

export const setCameraZoomRatio = async (cameraId: number, zoom: number): Promise<number> => {
	if (!ConfigCCTV.cameraControlEnabled) {
		return 0;
	}

	const clampedZoom = Math.round(clamp(zoom + Number.EPSILON, 0, 1) * 100) / 100;

	const url = new URL(ConfigCCTVApi.setCameraZoomRatio(cameraId), APIConfig.endpointURLBase);
	url.searchParams.append('zNorm', clampedZoom.toString());

	await APIRequest(
		new Request(url.href, {
			method: HTTPMethod.GET,
			headers: new Headers({
				...getAPIHeaders(),
			}),
		}),
	);

	return clampedZoom;
};

export const panCameraToClick = (cameraId: number) => (e: MouseEvent) => {
	if (!ConfigCCTV.cameraControlEnabled) {
		return;
	}

	const { width, height } = (e.currentTarget as HTMLVideoElement).getBoundingClientRect();
	const { layerX, layerY } = e;

	const xNorm = (2 * (layerX - width / 2)) / width;
	const yNorm = (2 * (height / 2 - layerY)) / height;

	if (isValidNumber(xNorm) && isValidNumber(yNorm)) {
		const url = new URL(
			ConfigCCTVApi.panTiltCameraToFieldOfView(cameraId),
			APIConfig.endpointURLBase,
		);
		url.searchParams.append('xNorm', xNorm.toFixed(2).toString());
		url.searchParams.append('yNorm', yNorm.toFixed(2).toString());

		void APIRequest(
			new Request(url.href, {
				method: HTTPMethod.GET,
				headers: new Headers({
					...getAPIHeaders(),
				}),
			}),
		);
	}
};
